import React from 'react';
import {
  Box, Button, ButtonText, ScrollView, Text, VStack,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { OralExamQuestionPager } from './OralExamQuestionPager';
import { useLinkedOralExamQuestions } from './linkedOralExamQuestions';

// Centred paragraphs read badly past ~75 characters; the question card keeps the full column.
const PARAGRAPH_MAX_WIDTH = 500;
// Centred text with a one-word last line looks broken; balance the lines on web.
const BALANCED_WRAP_SX = { _web: { textWrap: 'balance' } };

interface EssayStartScreenProps {
  taskId: string;
  title: string;
  onStart: () => void;
  isMobileView?: boolean;
  testID?: string;
}

/**
 * Opening page shown in place of the editor when a student opens an empty AI Tutor
 * essay in a module examined by oral exam: how rounds with "Get Feedback" work and
 * which oral exam questions the task prepares for. Start Working opens the editor.
 */
export const EssayStartScreen: React.FC<EssayStartScreenProps> = ({
  taskId,
  title,
  onStart,
  isMobileView = false,
  testID = 'essay-start-screen',
}) => {
  const { t } = useTranslation();
  const { status, questions } = useLinkedOralExamQuestions(taskId);

  const examLine = status === 'linked'
    ? t('student.task.essayStart.examLinked', { count: questions.length })
    : t('student.task.essayStart.examUnlinked');

  return (
    <ScrollView flex={1} contentContainerStyle={{ flexGrow: 1 }} testID={testID}>
      {/* Centred in the column, nudged above the midpoint: more room below than above. */}
      <VStack
        flex={1}
        justifyContent="center"
        alignItems="center"
        alignSelf="center"
        width="100%"
        maxWidth={560}
        pt={isMobileView ? '$6' : '$8'}
        pb={isMobileView ? '$6' : '$20'}
      >
        {/* Everything to read sits in one block; the exam question stands apart below it. */}
        <VStack space="md" width="100%" alignItems="center">
          {isMobileView ? null : (
            <Text size="xl" fontFamily="$heading" color="$textHeading" textAlign="center" testID={`${testID}-title`}>
              {title}
            </Text>
          )}
          <Text textAlign="center" maxWidth={PARAGRAPH_MAX_WIDTH} sx={BALANCED_WRAP_SX} testID={`${testID}-welcome`}>
            {t(isMobileView ? 'student.task.essayStart.welcomeMobile' : 'student.task.essayStart.welcome')}
          </Text>
          <Text
            textAlign="center"
            maxWidth={PARAGRAPH_MAX_WIDTH}
            mt="$2"
            sx={BALANCED_WRAP_SX}
            testID={`${testID}-exam`}
          >
            {examLine}
          </Text>
        </VStack>
        {status === 'linked' ? (
          <Box width="100%" mt={isMobileView ? '$8' : '$12'}>
            <OralExamQuestionPager questions={questions} isMobileView={isMobileView} />
          </Box>
        ) : null}
        <Box mt={isMobileView ? '$8' : '$10'}>
          <Button size="md" onPress={onStart} testID="essay-start-button">
            <ButtonText>{t('student.task.essayStart.startWorking')}</ButtonText>
          </Button>
        </Box>
      </VStack>
    </ScrollView>
  );
};

export default EssayStartScreen;
